import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { toast, Toaster } from "sonner";
import { ASSETS } from "@/lib/assets";
import { Download, LogOut, Search } from "lucide-react";
import { EpisodesCMS, GuestsCMS, SettingsCMS } from "@/components/admin/CMS";
import RetoAdmin from "@/components/admin/RetoCMS";
import ResourcesCMS from "@/components/admin/ResourcesCMS";

const STATUS_OPTIONS = ["INVITADO", "FORMULARIO RECIBIDO", "INVESTIGACIÓN", "PROGRAMADO", "GRABADO", "PUBLICADO"];

export function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const hash = location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) { navigate("/admin/login"); return; }
    const sid = m[1];
    (async () => {
      try {
        await api.post("/auth/session", { session_id: sid });
        window.history.replaceState(null, "", "/admin");
        navigate("/admin", { replace: true });
      } catch (e) {
        toast.error("No pudimos iniciar sesión");
        navigate("/admin/login");
      }
    })();
  }, []); // eslint-disable-line

  return (
    <div className="min-h-screen bg-mp-black flex items-center justify-center">
      <div className="text-mp-mute font-display text-sm tracking-[0.24em] uppercase">Verificando sesión…</div>
    </div>
  );
}

export function AdminLogin() {
  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <main data-testid="admin-login" className="min-h-screen bg-mp-black flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center">
        <img src={ASSETS.logo} alt="Modo Pro" width={80} height={80} className="mx-auto" />
        <div className="mp-eyebrow mt-10">Panel de Producción</div>
        <h1 className="mt-6 font-display font-bold text-white text-4xl leading-[1] tracking-[-0.02em]">
          Acceso <span className="text-mp-orange">Modo Pro</span>.
        </h1>
        <p className="mt-4 text-mp-mute text-sm">Ingresa con la cuenta autorizada del equipo de producción.</p>
        <button data-testid="admin-google-login" onClick={login} className="mp-btn-primary mt-10 w-full justify-center">
          Continuar con Google
        </button>
        <p className="mt-6 text-xs text-mp-dim tracking-wider">Acceso restringido — solo cuentas autorizadas.</p>
      </div>
    </main>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("submissions");
  const [subs, setSubs] = useState([]);
  const [stats, setStats] = useState(null);
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/auth/me");
        if (!r.data.is_admin) {
          toast.error("Cuenta no autorizada");
          navigate("/admin/login");
          return;
        }
        setUser(r.data);
      } catch {
        navigate("/admin/login");
      } finally {
        setChecking(false);
      }
    })();
  }, []); // eslint-disable-line

  const load = async () => {
    try {
      const [s, st] = await Promise.all([api.get("/admin/submissions"), api.get("/admin/stats")]);
      setSubs(s.data);
      setStats(st.data);
    } catch (e) {
      toast.error("Error cargando datos");
    }
  };
  useEffect(() => { if (user) load(); }, [user]); // eslint-disable-line

  const logout = async () => {
    await api.post("/auth/logout");
    navigate("/admin/login");
  };

  const filtered = subs.filter((s) => {
    const q = query.toLowerCase();
    const okQ = !q || [s.full_name, s.profession, s.company, s.email].some((v) => (v || "").toLowerCase().includes(q));
    const okF = filter === "all" || s.status === filter;
    return okQ && okF;
  });

  if (checking) {
    return <div className="min-h-screen bg-mp-black flex items-center justify-center text-mp-mute">Cargando…</div>;
  }

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-mp-black">
      <Toaster theme="dark" />
      <header className="border-b border-white/10 sticky top-0 z-40 bg-mp-black/95 backdrop-blur">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={ASSETS.logo} alt="Modo Pro" width={32} height={32} />
            <span className="font-display text-xs tracking-[0.32em] uppercase text-white">Producción / Modo <span className="text-mp-orange">Pro</span></span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-mp-mute text-sm hidden md:inline">{user?.email}</span>
            <button data-testid="admin-logout" onClick={logout} className="text-white/70 hover:text-mp-orange transition-colors flex items-center gap-2 text-xs font-display tracking-[0.2em] uppercase">
              <LogOut size={14} /> Salir
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1600px] px-6 lg:px-10 py-10">
        {/* Tabs */}
        <div className="flex flex-wrap gap-6 border-b border-white/10 mb-10">
          {[
            ["submissions", "Invitados"],
            ["reto", "Reto 🔥"],
            ["episodes", "Episodios"],
            ["public_guests", "Invitados Públicos"],
            ["resources", "Libros y Recursos"],
            ["settings", "Redes · Momentos · Contacto"],
          ].map(([k, l]) => (
            <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)} className={`py-4 font-display text-xs tracking-[0.24em] uppercase transition-colors ${tab === k ? "text-mp-orange border-b border-mp-orange" : "text-mp-mute hover:text-white"}`}>
              {l}
            </button>
          ))}
        </div>

        {tab === "episodes" && <EpisodesCMS />}
        {tab === "public_guests" && <GuestsCMS />}
        {tab === "settings" && <SettingsCMS />}
        {tab === "reto" && <RetoAdmin />}
        {tab === "resources" && <ResourcesCMS />}

        {tab === "submissions" && (
        <>
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-7 gap-px bg-white/10 mb-10">
          <StatCell label="Total" value={stats?.total || 0} highlight />
          {STATUS_OPTIONS.map((s) => (
            <StatCell key={s} label={s} value={stats?.by_status?.[s] || 0} />
          ))}
        </div>

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <h1 className="font-display font-bold text-white text-3xl md:text-4xl tracking-[-0.02em]">Invitados</h1>
          <div className="flex flex-wrap gap-2 items-center">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-mp-mute" />
              <input data-testid="admin-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar por nombre, profesión, email…" className="bg-mp-off border border-white/10 pl-9 pr-4 py-2 text-sm text-white focus:border-mp-orange outline-none w-72" />
            </div>
            <select data-testid="admin-filter" value={filter} onChange={(e) => setFilter(e.target.value)} className="bg-mp-off border border-white/10 px-3 py-2 text-sm text-white focus:border-mp-orange outline-none">
              <option value="all">Todos</option>
              {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>

        <div className="border border-white/10">
          <div className="grid grid-cols-12 gap-4 px-4 py-3 border-b border-white/10 text-[10px] tracking-[0.24em] uppercase text-mp-mute font-display">
            <div className="col-span-3">Invitado</div>
            <div className="col-span-3">Profesión</div>
            <div className="col-span-2">Empresa</div>
            <div className="col-span-2">Enviado</div>
            <div className="col-span-2">Estado</div>
          </div>
          {filtered.length === 0 && <div className="p-10 text-mp-mute text-sm">Sin envíos aún.</div>}
          {filtered.map((s) => (
            <button key={s.id} onClick={() => setSelected(s.id)} data-testid={`admin-row-${s.id}`} className="w-full text-left grid grid-cols-12 gap-4 px-4 py-4 border-b border-white/5 hover:bg-white/[0.03] transition-colors">
              <div className="col-span-3">
                <div className="text-white font-display">{s.full_name}</div>
                <div className="text-mp-mute text-xs">{s.email}</div>
              </div>
              <div className="col-span-3 text-white/80 text-sm self-center">{s.profession}</div>
              <div className="col-span-2 text-white/70 text-sm self-center">{s.company || "—"}</div>
              <div className="col-span-2 text-white/60 text-xs self-center">{new Date(s.submitted_at).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" })}</div>
              <div className="col-span-2 self-center">
                <span className="inline-flex px-3 py-1 border border-white/15 text-white/90 text-[10px] tracking-[0.2em] uppercase font-display">{s.status}</span>
              </div>
            </button>
          ))}
        </div>
        </>
        )}
      </div>

      {selected && <GuestDetailDrawer id={selected} onClose={() => { setSelected(null); load(); }} />}
    </main>
  );
}

function StatCell({ label, value, highlight }) {
  return (
    <div className={`p-4 ${highlight ? "bg-mp-orange/10" : "bg-mp-black"}`}>
      <div className={`font-display text-3xl ${highlight ? "text-mp-orange" : "text-white"}`}>{value}</div>
      <div className="text-mp-mute text-[10px] tracking-[0.2em] uppercase mt-1 font-display">{label}</div>
    </div>
  );
}

function GuestDetailDrawer({ id, onClose }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("perfil");

  const load = async () => {
    const r = await api.get(`/admin/submissions/${id}`);
    setData(r.data);
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const updateStatus = async (s) => {
    await api.put(`/admin/submissions/${id}/status`, { status: s });
    toast.success("Estado actualizado");
    load();
  };

  if (!data) return null;
  const files = data.files || [];
  const byCat = files.reduce((acc, f) => ((acc[f.category] ||= []).push(f), acc), {});
  const backend = process.env.REACT_APP_BACKEND_URL;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex" onClick={onClose}>
      <div className="ml-auto w-full max-w-[1100px] h-full bg-mp-black border-l border-white/10 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 bg-mp-black/95 backdrop-blur border-b border-white/10 px-8 py-5 flex items-center justify-between">
          <div>
            <div className="text-mp-orange text-[10px] tracking-[0.24em] uppercase font-display">{data.profession}</div>
            <h2 className="font-display text-2xl text-white tracking-[-0.02em] mt-1">{data.full_name}</h2>
          </div>
          <div className="flex items-center gap-3">
            <select data-testid="detail-status-select" value={data.status} onChange={(e) => updateStatus(e.target.value)} className="bg-mp-off border border-white/10 px-3 py-2 text-xs text-white focus:border-mp-orange outline-none font-display tracking-[0.2em] uppercase">
              {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="detail-close">✕</button>
          </div>
        </div>

        <div className="border-b border-white/10 px-8 flex gap-6 overflow-x-auto">
          {["perfil", "historia", "entrevista", "media", "fotos", "produccion"].map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`detail-tab-${t}`} className={`py-4 font-display text-xs tracking-[0.24em] uppercase transition-colors ${tab === t ? "text-mp-orange border-b border-mp-orange" : "text-mp-mute hover:text-white"}`}>{t}</button>
          ))}
        </div>

        <div className="p-8 space-y-8">
          {tab === "perfil" && (
            <>
              <KV label="Presentación" value={data.presentation_name} />
              <KV label="Email" value={data.email} />
              <KV label="Teléfono" value={data.phone} />
              <KV label="Ciudad" value={data.city} />
              <KV label="País de origen" value={data.country_origin} />
              <KV label="Empresa" value={data.company} />
              <KV label="Instagram" value={data.instagram} link={data.instagram && (data.instagram.startsWith("http") ? data.instagram : `https://instagram.com/${data.instagram.replace("@","")}`)} />
              <KV label="LinkedIn" value={data.linkedin} link={data.linkedin} />
              <KV label="Website" value={data.website} link={data.website} />
              <KV label="Descripción en una frase" value={data.one_line} big />
            </>
          )}
          {tab === "historia" && (
            <>
              <KV label="Trayectoria y experiencia como experto(a)" value={data.trajectory_expertise} big />
              <KV label="Momento/decisión que cambió su manera de entender el tema" value={data.trajectory_pivot} big />
              <KV label="Experiencia/proyectos/resultados que mejor representan su trayectoria" value={data.trajectory_representative} big />
              <KV label="Rol actual y área de especialidad" value={data.trajectory_current_role} big />
              <KV label="Tamaño/operación del negocio (empleados, clientes, mercados, etc.)" value={data.trajectory_business_size} big />
              <KV label="Opinión con la que muchos no estarían de acuerdo" value={data.trajectory_controversial} big />
              {/* Legacy fields (mostrar solo si tienen contenido) */}
              <KV label="Su historia (legacy)" value={data.story_origin} big />
              <KV label="Historia de inmigración (legacy)" value={data.immigration_story} big />
              <KV label="Primeros años en USA (legacy)" value={data.early_years_usa} big />
              <KV label="Primer trabajo/proyecto (legacy)" value={data.first_job} big />
              <KV label="Momento oscuro (legacy)" value={data.dark_moment} big />
              <KV label="Punto de inflexión (legacy)" value={data.turning_point} big />
              <KV label="Mayor fracaso/aprendizaje (legacy)" value={data.biggest_failure} big />
              <KV label="Mayor logro (legacy)" value={data.biggest_achievement} big />
              <KV label="Decisión de mayor impacto (legacy)" value={data.key_decision} big />
            </>
          )}
          {tab === "entrevista" && (
            <>
              <div>
                <div className="mp-label">Temas propuestos</div>
                <ul className="list-disc pl-5 text-white/90 space-y-1">
                  {(data.topics || []).filter(Boolean).map((t, i) => <li key={i}>{t}</li>)}
                </ul>
              </div>
              <KV label="Historia poco contada" value={data.untold_story} big />
              <KV label="Malentendido sobre su industria" value={data.industry_misconception} big />
              <KV label="Pregunta que le gustaría" value={data.desired_question} big />
              <KV label="Ejemplo con números reales" value={data.numeric_example} big />
              {data.topics_to_avoid && (
                <div className="border border-mp-orange/50 bg-mp-orange/5 p-6" data-testid="detail-avoid">
                  <div className="mp-eyebrow">Temas que prefiere NO abordar</div>
                  <p className="mt-3 text-white leading-relaxed whitespace-pre-wrap">{data.topics_to_avoid}</p>
                </div>
              )}
            </>
          )}
          {tab === "media" && (
            <>
              <KV label="¿Ha participado en medios?" value={data.previous_media ? "Sí" : "No"} />
              <div>
                <div className="mp-label">Enlaces relevantes</div>
                <ul className="space-y-1 text-mp-orange">
                  {(data.previous_media_urls || []).filter(Boolean).map((u, i) => <li key={i}><a href={u} target="_blank" rel="noreferrer" className="mp-link-underline break-all">{u}</a></li>)}
                </ul>
              </div>
              <KV label="Foto — historia detrás (legacy)" value={data.photo_story_caption} big />
            </>
          )}
          {tab === "fotos" && (
            <>
              {Object.keys(byCat).length === 0 && <p className="text-mp-mute">Sin fotos.</p>}
              {Object.entries(byCat).map(([cat, arr]) => (
                <div key={cat}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="mp-eyebrow">{cat}</div>
                    <span className="text-mp-mute text-xs">{arr.length} archivo(s)</span>
                  </div>
                  <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                    {arr.map((f) => (
                      <a key={f.id} href={`${backend}/api/admin/files/${f.id}`} target="_blank" rel="noreferrer" download data-testid={`detail-file-${f.id}`} className="relative aspect-square border border-white/10 overflow-hidden group">
                        {f.content_type?.startsWith("image") ? (
                          <img src={`${backend}/api/admin/files/${f.id}`} alt="" className="h-full w-full object-cover" />
                        ) : f.content_type?.startsWith("audio") ? (
                          <div className="h-full w-full flex flex-col items-center justify-center bg-mp-charcoal text-mp-orange">
                            <Mic /> <span className="text-[10px] mt-2 tracking-[0.2em] uppercase">Audio</span>
                          </div>
                        ) : null}
                        <div className="absolute bottom-1 right-1 bg-black/70 p-1 opacity-0 group-hover:opacity-100 transition"><Download size={12} className="text-white" /></div>
                      </a>
                    ))}
                  </div>
                </div>
              ))}
            </>
          )}
          {tab === "produccion" && (
            <>
              <KV label="Pronunciación (texto)" value={data.pronunciation_text} />
              {data.pronunciation_audio_path && (
                <div>
                  <div className="mp-label">Pronunciación (audio)</div>
                  {(byCat.pronunciation || []).map((f) => (
                    <audio key={f.id} controls src={`${backend}/api/admin/files/${f.id}`} className="mt-2" />
                  ))}
                </div>
              )}
              <KV label="Proyectos a conocer" value={data.projects_to_know} big />
              <div>
                <div className="mp-label">Links adicionales</div>
                <ul className="space-y-1 text-mp-orange">
                  {(data.additional_links || []).filter(Boolean).map((u, i) => <li key={i}><a href={u} target="_blank" rel="noreferrer" className="mp-link-underline break-all">{u}</a></li>)}
                </ul>
              </div>
              <KV label="Notas para producción" value={data.additional_info} big />
              <KV label="Consentimiento" value={data.consent ? "Aceptado" : "No aceptado"} />
              <KV label="Enviado" value={new Date(data.submitted_at).toLocaleString("es-MX")} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function KV({ label, value, big, link }) {
  if (!value) return null;
  return (
    <div>
      <div className="mp-label">{label}</div>
      {link ? (
        <a href={link} target="_blank" rel="noreferrer" className={`text-mp-orange mp-link-underline break-all ${big ? "block" : "inline"}`}>{value}</a>
      ) : (
        <p className={`text-white/90 ${big ? "leading-relaxed whitespace-pre-wrap" : ""}`}>{value}</p>
      )}
    </div>
  );
}

function Mic() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="9" y="3" width="6" height="12" rx="3"/><path d="M5 11a7 7 0 0 0 14 0"/><path d="M12 18v3"/></svg>;
}
